import React, { useState, useEffect } from "react";
import { Dialog, DialogSurface, makeStyles } from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";

import { tokens } from "../../theme";
import { brand } from "@brand";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingVerticalXs: tokens.primitives["spacing-Vertical-XS"],
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingVerticalM: tokens.primitives["spacing-Vertical-M"],
    spacingVerticalL: tokens.primitives["spacing-Vertical-L"],
    spacingVerticalXl: tokens.primitives["spacing-Vertical-XL"],
    spacingHorizontalS: tokens.primitives["spacing-Horizontal-S"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],
    spacingHorizontalL: tokens.primitives["spacing-Horizontal-L"],
    spacingHorizontalXl: tokens.primitives["spacing-Horizontal-XL"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontSizeBase400: tokens.primitives["font-Size-Base-400"],
    fontSizeBase600: tokens.primitives["font-Size-Base-600"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],
    fontWeightMedium: tokens.primitives["font-Weight-Medium"],
    fontWeightSemiBold: tokens.primitives["font-Weight-SemiBold"],
    lineHeightBase300: tokens.primitives["lineHeight-Base-300"],
    lineHeightBase400: tokens.primitives["lineHeight-Base-400"],
    lineHeightBase500: tokens.primitives["lineHeight-Base-500"],

    // Colors
    themePrimary: tokens.palette["theme-Primary"],
    themeDarkAlt: tokens.palette["theme-DarkAlt"],
    neutralPrimary: tokens.semantic["neutral-Primary"],
    neutralSecondary: tokens.semantic["neutral-Secondary"],
    neutralTertiaryAlt: tokens.semantic["neutral-TertiaryAlt"],
    neutralWhite: tokens.semantic["neutral-White"],
    neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],
    neutralLighter: tokens.semantic["neutral-Lighter"],
    dialogBackground: tokens.components.dialogBackground,

    // Radius
    radiusMed: tokens.primitives["stroke-Radius-Medium"],
    radiusLarge: tokens.primitives["stroke-Radius-Large"],
    radiusXLarge: tokens.primitives["stroke-Radius-XLarge"],
    radiusXXLarge: tokens.primitives["stroke-RadiusXxl"],

    // Shadows
    shadowFloatingSmall: tokens.shadows["shadow-Floating-Small"],
    shadowFloatingMedium: tokens.shadows["shadow-Floating-Medium"],
    shadowRestingSmall: tokens.shadows["shadow-Resting-Small"],
    shadowRestingMedium: tokens.shadows["shadow-Resting-Medium"],

    // Buttons
    btnPrimaryBg: tokens.buttons.buttonSolidPrimaryBackground,
    btnPrimaryFg: tokens.buttons.buttonSolidPrimaryForeground,
    btnPrimaryBgHover: tokens.buttons.buttonSolidPrimaryBackgroundHover,
    btnPrimaryBgPressed: tokens.buttons.buttonSolidPrimaryBackgroundPressed,
    btnOutlinedSecondaryStroke: tokens.buttons.buttonOutlinedSecondaryStroke,
    btnOutlinedSecondaryBg: tokens.buttons.buttonOutlinedSecondaryBackground,
    btnOutlinedSecondaryBgHover: tokens.buttons.buttonOutlinedSecondaryBackgroundHover,
    btnOutlinedSecondaryBgPressed: tokens.buttons.buttonOutlinedSecondaryBackgroundPressed,
    btnOutlinedSecondaryFg: tokens.buttons.buttonOutlinedSecondaryForeground,

    // Stroke
    strokeFocus: tokens.primitives.strokeFocus
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    // Welcome Dialog Styles
    dialogSurface: {
        maxWidth: "520px",
        width: "90vw",
        padding: 0,
        borderRadius: t.radiusXXLarge,
        boxShadow: t.shadowFloatingMedium,
        overflow: "hidden",
        backgroundColor: t.dialogBackground
    },

    dialogContent: {
        display: "flex",
        flexDirection: "column",
        padding: t.spacingHorizontalXl
    },

    dialogHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: t.spacingVerticalM
    },

    dialogTitle: {
        color: t.themePrimary,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase600,
        fontWeight: t.fontWeightSemiBold as unknown as number,
        margin: 0,
        lineHeight: t.lineHeightBase500
    },

    closeButton: {
        background: "none",
        border: "none",
        padding: t.spacingVerticalXs,
        cursor: "pointer",
        color: t.neutralTertiaryAlt,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: t.radiusMed,
        transitionProperty: "color, background-color",
        transitionDuration: "0.15s",
        flexShrink: 0,

        "&:hover": {
            color: t.neutralSecondary,
            backgroundColor: t.neutralLighterAlt
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    },

    dialogBody: {
        color: t.neutralPrimary,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightBase400,
        marginBottom: t.spacingVerticalL
    },

    paragraph: {
        margin: 0,
        marginBottom: t.spacingVerticalM
    },

    paragraphLast: {
        margin: 0
    },

    dialogActions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: t.spacingHorizontalS
    },

    // Teaching Bubble Styles
    teachingBubbleOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        pointerEvents: "none"
    },

    // Update these styles in useStyles:

    teachingBubble: {
        position: "absolute",
        backgroundColor: t.dialogBackground,
        borderRadius: t.radiusXXLarge,
        boxShadow: t.shadowRestingSmall,
        border: `1px solid ${t.neutralLighter}`,
        padding: t.spacingHorizontalL,
        maxWidth: "340px",
        width: "calc(100vw - 32px)",
        pointerEvents: "auto",
        zIndex: 1001,
        boxSizing: "border-box",
        overflow: "visible",

        "@media (min-width: 480px)": {
            width: "max-content"
        }
    },

    // Arrow with shadow
    teachingBubbleArrow: {
        position: "absolute",
        width: "20px",
        height: "20px",
        backgroundColor: t.dialogBackground,
        border: `1px solid ${t.neutralLighter}`,
        borderTop: "none",
        borderRight: "none",
        boxShadow: t.shadowRestingSmall,
        transform: "rotate(-45deg)",
        bottom: "-11px",
        zIndex: -1
    },

    teachingBubbleArrowTop: {
        transform: "rotate(135deg)",
        top: "-11px",
        bottom: "auto"
    },

    teachingBubbleArrowCover: {
        position: "absolute",
        width: "36px",
        height: "12px",
        backgroundColor: t.dialogBackground,
        bottom: "-1px", // Position at the very edge, covering the border
        zIndex: 1,
        pointerEvents: "none" // Allow clicks to pass through
    },

    teachingBubbleArrowCoverTop: {
        top: "-1px",
        bottom: "auto"
    },

    teachingBubbleHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: t.spacingVerticalS
    },

    teachingBubbleTitle: {
        color: t.themePrimary,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase400,
        fontWeight: t.fontWeightSemiBold as unknown as number,
        margin: 0,
        lineHeight: t.lineHeightBase400
    },

    teachingBubbleBody: {
        color: t.neutralPrimary,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightRegular as unknown as number,
        lineHeight: t.lineHeightBase300,
        marginBottom: t.spacingVerticalM
    },

    teachingBubbleFooter: {
        display: "flex",
        justifyContent: "flex-end",
        gap: t.spacingHorizontalS
    },

    // Button Styles
    primaryButton: {
        padding: "6px 16px",
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightMedium as unknown as number,
        borderRadius: t.radiusLarge,
        backgroundColor: t.btnPrimaryBg,
        color: t.btnPrimaryFg,
        border: "none",
        cursor: "pointer",
        transitionProperty: "background-color",
        transitionDuration: "0.15s",

        "&:hover": {
            backgroundColor: t.btnPrimaryBgHover
        },

        "&:active": {
            backgroundColor: t.btnPrimaryBgPressed
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    },

    secondaryButton: {
        padding: "6px 16px",
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightMedium as unknown as number,
        borderRadius: t.radiusMed,
        backgroundColor: t.btnOutlinedSecondaryBg,
        color: t.neutralSecondary,
        border: `1px solid ${t.neutralLighter}`,
        cursor: "pointer",
        transitionProperty: "background-color, border-color",
        transitionDuration: "0.15s",

        "&:hover": {
            backgroundColor: t.btnOutlinedSecondaryBgHover
        },

        "&:active": {
            backgroundColor: t.btnOutlinedSecondaryBgPressed
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    }
});

// =============================================================================
// TEACHING BUBBLE COMPONENT - Responsive positioning
// =============================================================================

interface TeachingBubbleProps {
    targetId: string;
    title: string;
    children: React.ReactNode;
    onNext: () => void;
    onPrev?: () => void;
    onClose: () => void;
    nextLabel?: string;
    prevLabel?: string;
    position?: "above" | "below";
}

const TeachingBubble: React.FC<TeachingBubbleProps> = ({
    targetId,
    title,
    children,
    onNext,
    onPrev,
    onClose,
    nextLabel = "Next",
    prevLabel = "Previous",
    position = "above"
}) => {
    const styles = useStyles();
    const [bubblePosition, setBubblePosition] = useState({ top: 0, left: 0 });
    const [arrowPosition, setArrowPosition] = useState(50); // Percentage from left
    const [bubbleRef, setBubbleRef] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        const updatePosition = () => {
            const targetElement = document.getElementById(targetId);
            if (!targetElement) return;

            const rect = targetElement.getBoundingClientRect();
            const targetCenterX = rect.left + rect.width / 2;

            // Get viewport dimensions
            const viewportWidth = window.innerWidth;
            const padding = 16; // Min distance from viewport edge

            // Get bubble dimensions
            const bubbleWidth = bubbleRef?.offsetWidth || 340;
            const bubbleHeight = bubbleRef?.offsetHeight || 150;
            const arrowOffset = 15;

            // Calculate ideal left position (centered on target)
            let left = targetCenterX - bubbleWidth / 2;

            // Constrain to viewport
            const minLeft = padding;
            const maxLeft = viewportWidth - bubbleWidth - padding;

            // Clamp the position
            left = Math.max(minLeft, Math.min(maxLeft, left));

            // Calculate arrow position as percentage
            // Arrow should point to target center relative to bubble position
            const arrowLeftPx = targetCenterX - left;
            const arrowPercent = (arrowLeftPx / bubbleWidth) * 100;
            // Clamp arrow between 10% and 90% to stay within bubble
            const clampedArrowPercent = Math.max(10, Math.min(90, arrowPercent));

            setArrowPosition(clampedArrowPercent);

            // Calculate top position
            let top: number;
            if (position === "above") {
                top = rect.top - bubbleHeight - arrowOffset;
            } else {
                top = rect.bottom + arrowOffset;
            }

            setBubblePosition({ top, left });
        };

        updatePosition();

        // Recalculate after render
        const timer = setTimeout(updatePosition, 50);

        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition);

        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition);
            clearTimeout(timer);
        };
    }, [targetId, position, bubbleRef]);

    const targetElement = document.getElementById(targetId);
    if (!targetElement) return null;

    return (
        <div className={styles.teachingBubbleOverlay}>
            <div
                ref={setBubbleRef}
                className={styles.teachingBubble}
                style={{ top: bubblePosition.top, left: bubblePosition.left }}
                role="dialog"
                aria-labelledby={`teaching-bubble-title-${targetId}`}
                aria-describedby={`teaching-bubble-body-${targetId}`}
            >
                {/* Arrow */}
                <div
                    className={`${styles.teachingBubbleArrow} ${position === "below" ? styles.teachingBubbleArrowTop : ""}`}
                    style={{ left: `${arrowPosition}%`, transform: `translateX(-50%) rotate(${position === "below" ? "135deg" : "-45deg"})` }}
                />
                {/* Cover to hide arrow shadow inside bubble */}
                <div
                    className={`${styles.teachingBubbleArrowCover} ${position === "below" ? styles.teachingBubbleArrowCoverTop : ""}`}
                    style={{ left: `calc(${arrowPosition}% - 18px)` }}
                />

                <div className={styles.teachingBubbleHeader}>
                    <h3 id={`teaching-bubble-title-${targetId}`} className={styles.teachingBubbleTitle}>
                        {title}
                    </h3>
                    <button className={styles.closeButton} onClick={onClose} aria-label="Close tour">
                        <Dismiss24Regular />
                    </button>
                </div>

                <div id={`teaching-bubble-body-${targetId}`} className={styles.teachingBubbleBody}>
                    {children}
                </div>

                <div className={styles.teachingBubbleFooter}>
                    {onPrev && (
                        <button className={styles.secondaryButton} onClick={onPrev}>
                            {prevLabel}
                        </button>
                    )}
                    <button className={styles.primaryButton} onClick={onNext}>
                        {nextLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const FeatureTour = (props: { isTermsAccepted: boolean; isTourCompleted: boolean; onTourEnd: () => void }) => {
    const styles = useStyles();
    const [teachingBubbleVisible, setTeachingBubbleVisible] = useState(true);
    const [teachingBubbleCurrPage, setTeachingBubbleCurrPage] = useState(1);

    const nextTeachingBubblePage = () => {
        setTeachingBubbleCurrPage(teachingBubbleCurrPage + 1);
    };

    const prevTeachingBubblePage = () => {
        setTeachingBubbleCurrPage(teachingBubbleCurrPage - 1);
    };

    const hideTeachingBubble = () => {
        props.onTourEnd();
        setTeachingBubbleVisible(false);
    };

    const shouldShowTour = props.isTermsAccepted && !props.isTourCompleted && teachingBubbleVisible;

    return (
        <>
            {/* Welcome Dialog - Step 1 */}
            {shouldShowTour && teachingBubbleCurrPage === 1 && (
                <Dialog open={true} modalType="alert">
                    <DialogSurface className={styles.dialogSurface} aria-labelledby="welcome-title" aria-describedby="welcome-body">
                        <div className={styles.dialogContent}>
                            <div className={styles.dialogHeader}>
                                <h2 id="welcome-title" className={styles.dialogTitle}>
                                    Welcome to {brand.name}!
                                </h2>
                                <button className={styles.closeButton} onClick={hideTeachingBubble} aria-label="Skip tour">
                                    <Dismiss24Regular />
                                </button>
                            </div>

                            <div id="welcome-body" className={styles.dialogBody}>
                                {brand.tour.welcomeParagraphs.map((text, i, all) => (
                                    <p key={i} className={i === all.length - 1 ? styles.paragraphLast : styles.paragraph}>
                                        {text}
                                    </p>
                                ))}
                            </div>

                            <div className={styles.dialogActions}>
                                <button className={styles.secondaryButton} onClick={hideTeachingBubble}>
                                    Skip
                                </button>
                                <button className={styles.primaryButton} onClick={nextTeachingBubblePage}>
                                    Start tour
                                </button>
                            </div>
                        </div>
                    </DialogSurface>
                </Dialog>
            )}

            {/* Teaching Bubble - Step 2: Chat Input */}
            {shouldShowTour && teachingBubbleCurrPage === 2 && (
                <TeachingBubble
                    targetId="chatInputContainer"
                    title="Input clear and detailed questions"
                    onNext={nextTeachingBubblePage}
                    onPrev={prevTeachingBubblePage}
                    onClose={hideTeachingBubble}
                    position="above"
                >
                    The more specific you are, the better your response will be.
                </TeachingBubble>
            )}

            {/* Teaching Bubble - Step 3: Clear Chat */}
            {shouldShowTour && teachingBubbleCurrPage === 3 && (
                <TeachingBubble
                    targetId="clearChatBtn"
                    title="Clear the chat for new topics"
                    onNext={hideTeachingBubble}
                    onPrev={prevTeachingBubblePage}
                    onClose={hideTeachingBubble}
                    nextLabel="End tour"
                    position="above"
                >
                    {brand.tour.clearChat}
                </TeachingBubble>
            )}
        </>
    );
};

export default FeatureTour;
